import { fromJS, Map, Record } from 'immutable';
import {
  generateKeys,
  generateSchema,
  keyIn,
} from '../../../utils/helpers/model.helper';

export enum PaymentTransactionAvailableActionsEnum {
  none = 'none',
  delete_manual_payment = 'delete_manual_payment',
  refund = 'refund',
  unmatch = 'unmatch',
}

export enum PaymentTransactionCreditCardTypesEnum {
  unknown = 'unknown',
  visa = 'visa',
  amex = 'amex',
  master_card = 'master_card',
  discover = 'discover',
  jcb = 'jcb',
  diners_club = 'diners_club',
  paypal = 'paypal',
}

export enum PaymentTransactionTypesEnum {
  payment = 'payment',
  refund = 'refund',
  dispute = 'dispute',
}

export enum PaymentTransactionMethodsEnum {
  paypal_ec = 'paypal_ec',
  credit_card = 'credit_card',
  manual_payment = 'manual_payment',
  apple_pay = 'apple_pay',
  bank_transfer = 'bank_transfer',
}

export enum PaymentTransactionStatusesEnum {
  created = 'created',
  succeeded = 'succeeded',
  failed = 'failed',
  pending = 'pending',
  cancelled = 'cancelled',
}

export const PAYMENT_TRANSACTION_SCHEMA = Object.freeze({
  TRANSACTION_ID: '',
  PARENT_TRANSACTION_ID: null, // ''
  CURRENCY_CODE: '',
  AMOUNT: 0,
  FPT_FEE_AMOUNT: 0,
  TIP_AMOUNT: 0,
  DATETIME: null, // i2g-date-time (yyyy-mm-dd'T'hh:mm:ssZ)
  TYPE: '',
  METHOD: '',
  METHOD_DETAIL: null, // ''
  STATUS: '',
  DESCRIPTION: '',
  CREDIT_CARD_TYPE: PaymentTransactionCreditCardTypesEnum.unknown,
  CARD_LAST_FOUR: null, // ''
  AVAILABLE_ACTION: PaymentTransactionAvailableActionsEnum.none,
  REFUNDABLE_UNTIL: null, // i2g-date-time (yyyy-mm-dd'T'hh:mm:ssZ)
  PAYER_EMAIL: null, // ''
  IS_DEPOSIT: false,
});
const RECORD = generateSchema(PAYMENT_TRANSACTION_SCHEMA);
const KEYS: any = generateKeys(PAYMENT_TRANSACTION_SCHEMA);

export class PaymentTransaction extends Record(RECORD) {
  static KEYS: any = KEYS;

  constructor(props?: any) {
    super(props && props.toMap ? props.toMap() : fromJS(props || {}));
  }

  get transactionId(): string {
    return this.get(KEYS.TRANSACTION_ID);
  }

  get parentTransactionId(): string {
    return this.get(KEYS.PARENT_TRANSACTION_ID);
  }

  get currencyCode(): string {
    return this.get(KEYS.CURRENCY_CODE);
  }

  get amount(): number {
    return this.get(KEYS.AMOUNT);
  }

  get datetime(): string {
    return this.get(KEYS.DATETIME);
  }

  get type(): PaymentTransactionTypesEnum {
    return this.get(KEYS.TYPE);
  }

  get method(): PaymentTransactionMethodsEnum {
    return this.get(KEYS.METHOD);
  }

  get methodDetail(): string {
    return this.get(KEYS.METHOD_DETAIL);
  }

  get description(): string {
    return this.get(KEYS.DESCRIPTION);
  }

  get availableAction(): PaymentTransactionAvailableActionsEnum {
    return this.get(KEYS.AVAILABLE_ACTION);
  }

  get creditCardType(): PaymentTransactionCreditCardTypesEnum {
    return this.get(KEYS.CREDIT_CARD_TYPE);
  }

  get cardLastFour(): string {
    return this.get(KEYS.CARD_LAST_FOUR);
  }

  get payerEmail(): string {
    return this.get(KEYS.PAYER_EMAIL);
  }

  get status(): PaymentTransactionStatusesEnum {
    return this.get(KEYS.STATUS);
  }

  get refundableUntil(): string {
    return this.get(KEYS.REFUNDABLE_UNTIL);
  }

  get isDeposit(): boolean {
    return this.get(KEYS.IS_DEPOSIT);
  }

  isRefund(): boolean {
    return this.type === PaymentTransactionTypesEnum.refund;
  }

  isPayment(): boolean {
    return this.type === PaymentTransactionTypesEnum.payment;
  }

  isDispute(): boolean {
    return this.type === PaymentTransactionTypesEnum.dispute;
  }

  isManualPayment(): boolean {
    return this.method === PaymentTransactionMethodsEnum.manual_payment;
  }

  isBankTransferTransaction(): boolean {
    return this.method === PaymentTransactionMethodsEnum.bank_transfer;
  }

  isPayPalTransaction(): boolean {
    return this.method === PaymentTransactionMethodsEnum.paypal_ec;
  }

  isCreditCardTransaction(): boolean {
    const transactionMethod = this.method;

    return (
      transactionMethod === PaymentTransactionMethodsEnum.credit_card ||
      transactionMethod === PaymentTransactionMethodsEnum.apple_pay
    );
  }

  isRefundable(): boolean {
    return (
      this.availableAction === PaymentTransactionAvailableActionsEnum.refund ||
      (this.isPayPalTransaction() && this.isPending())
    );
  }

  isDeletable(): boolean {
    return (
      this.availableAction ===
      PaymentTransactionAvailableActionsEnum.delete_manual_payment
    );
  }

  isUnmatchable(): boolean {
    return (
      this.availableAction === PaymentTransactionAvailableActionsEnum.unmatch
    );
  }

  isPending(): boolean {
    return this.status === PaymentTransactionStatusesEnum.pending;
  }

  isFailed(): boolean {
    return this.status === PaymentTransactionStatusesEnum.failed;
  }

  /**
   * Extract TransactionPayload from PaymentTransaction
   */
  transactionItemPayload(): Map<string, any> {
    return this.toMap().filter(
      keyIn(
        'transaction_id',
        'amount',
        'method_detail',
        'description',
        'datetime'
      )
    ) as Map<string, any>; // pick
  }
}
